import {
  getAllPublicGenies,
  getPublicGenieById,
  getPublicGeniesForIndustry,
} from "@/data/discovery/genie-catalog";
import { getBrandForSubcategory } from "@/data/genies/brands";
import { getBrandForPersona } from "@/data/genies/persona-brands";
import { supabase } from "@/integrations/supabase/client";
import { TokenService } from "@/services/auth/TokenService";
import { Agent } from "@/types/agent/agent";
import { GenieBranding } from "@/types/ui/branding";

/**
 * DemoGenieService – works with the agents table directly.
 *
 * Demo genies are regular agents with `is_demo_genie = true` and a `persona_id`
 * linking them back to the persona template in the genie registry.
 */
export class DemoGenieService {
  // =========================================================================
  // Query Methods
  // =========================================================================

  /**
   * Get a demo agent by persona ID with its agent_page
   */
  static async getDemoAgentByPersonaId(personaId: string): Promise<Agent | null> {
    // DEPRECATED: agent_pages table is obsolete and scheduled for deletion — do not add new usages.
    const { data, error } = await supabase
      .from("agents")
      .select("*, agent_pages(*)")
      .eq("is_demo_genie", true)
      .eq("persona_id", personaId)
      .not("elevenlabs_id", "is", null)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error("Error fetching demo agent:", error);
      return null;
    }
    return data as Agent | null;
  }

  /**
   * Get all demo agents that are active
   */
  static async getActiveDemoAgents(): Promise<Agent[]> {
    const { data, error } = await supabase
      .from("agents")
      .select("*")
      .eq("is_demo_genie", true)
      .eq("is_active", true)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching active demo agents:", error);
      return [];
    }
    return (data as Agent[]) || [];
  }

  // =========================================================================
  // Provisioning Methods
  // =========================================================================

  /**
   * Get or provision a demo agent (lazy provisioning).
   * If the demo agent already exists and has an elevenlabs_id, returns it.
   * If not, triggers provisioning via edge function.
   */
  static async getOrProvisionDemoAgent(personaId: string): Promise<Agent> {
    // Check if already provisioned
    const existing = await this.getDemoAgentByPersonaId(personaId);

    if (existing?.elevenlabs_id) {
      // Re-embed branding if agent_pages is missing logo/branding data
      // (handles agents provisioned before branding sync was implemented)
      // DEPRECATED: agent_pages table is obsolete and scheduled for deletion — do not add new usages.
      const pageBranding = Array.isArray(existing.agent_pages)
        ? (existing.agent_pages[0] as any)?.branding
        : (existing.agent_pages as any)?.branding;
      if (!pageBranding?.logoUrl) {
        this.reEmbedBranding(existing).catch((err) =>
          console.warn("Background re-embed failed (non-blocking):", err),
        );
      }
      return existing;
    }

    // If an agent exists but has no elevenlabs_id, it's still provisioning
    if (existing && !existing.elevenlabs_id) {
      return existing;
    }

    const persona = getPublicGenieById(personaId);
    if (!persona) {
      throw new Error(`Persona not found: ${personaId}`);
    }

    return this.provisionDemoAgent(persona.id);
  }

  /**
   * Provision a single demo agent from a persona template.
   * Calls the handle-agent edge function which creates both the
   * agent record in our DB and the ElevenLabs agent.
   */
  static async provisionDemoAgent(personaId: string): Promise<Agent> {
    try {
      const token = await TokenService.getAccessToken();
      const { data, error } = await supabase.functions.invoke("handle-agent", {
        body: {
          action: "demo-provision",
          personaId,
        },
        ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
      });

      if (error) {
        // Try to extract detailed error from response body
        let detail = error.message || "Edge function failed";
        if (data) {
          detail += ` | Response: ${JSON.stringify(data)}`;
        }
        console.error("Edge function error detail:", { error, data });
        throw new Error(detail);
      }
      if (!data?.success) throw new Error(data?.message || "Provisioning failed");

      // The edge function should have created the agent with is_demo_genie=true
      // and persona_id set. Fetch and return it.
      const agent = await this.getDemoAgentByPersonaId(personaId);
      if (!agent) throw new Error("Agent was not created during provisioning");

      // Upload brand logo and update branding before returning
      try {
        await this.uploadBrandLogo(agent, personaId);
        // Re-fetch agent to get updated branding with logo URL
        const updatedAgent = await this.getDemoAgentByPersonaId(personaId);
        if (updatedAgent) return updatedAgent;
      } catch (err) {
        console.warn("Brand logo upload failed (non-blocking):", err);
      }

      return agent;
    } catch (error) {
      console.error("Failed to provision demo agent:", error);
      throw error;
    }
  }

  /**
   * Upload the pre-generated brand logo to Supabase Storage
   * and update the agent's branding with the logo URL.
   */
  private static async uploadBrandLogo(agent: Agent, personaId: string): Promise<void> {
    const persona = getPublicGenieById(personaId);
    const brand =
      getBrandForPersona(personaId) ||
      (persona ? getBrandForSubcategory(persona.subcategoryId) : undefined);
    if (!brand) return;

    const safeName = brand.brandName.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    const logoFileName = `${safeName}-stacked.png`;
    const localLogoPath = `/brand-logos/${logoFileName}`;

    try {
      // Fetch the logo from the public folder
      const response = await fetch(localLogoPath);
      if (!response.ok) {
        console.warn(`Brand logo not found at ${localLogoPath}, skipping upload`);
        return;
      }

      const blob = await response.blob();
      const storagePath = `brand-logos/${agent.id}/${logoFileName}`;

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("genie-images")
        .upload(storagePath, blob, {
          cacheControl: "31536000",
          upsert: true,
          contentType: "image/png",
        });

      if (uploadError) {
        console.error("Logo upload failed:", uploadError);
        return;
      }

      // Get the public URL with cache-busting param so browsers always fetch fresh content
      const {
        data: { publicUrl },
      } = supabase.storage.from("genie-images").getPublicUrl(uploadData.path);
      const logoUrl = `${publicUrl}?v=${Date.now()}`;

      // Merge logo URL into existing branding
      const existingBranding = (agent.branding as Partial<GenieBranding>) || {};
      const updatedBranding: Partial<GenieBranding> = {
        ...existingBranding,
        logoUrl,
      };

      // Update the agent record with the logo URL in branding
      const { error: updateError } = await supabase
        .from("agents")
        .update({ branding: updatedBranding })
        .eq("id", agent.id);

      if (updateError) {
        console.error("Failed to update agent branding with logo:", updateError);
        return;
      }

      // Also update the agent_pages record with the logo and full branding
      // DEPRECATED: agent_pages table is obsolete and scheduled for deletion — do not add new usages.
      const { error: pageError } = await supabase
        .from("agent_pages")
        .update({
          genie_image_url: logoUrl,
          branding: updatedBranding,
        })
        .eq("elevenlabs_agent_id", agent.elevenlabs_id);

      if (pageError) {
        console.warn("Failed to update agent_pages with logo (may not exist yet):", pageError);
      }

      console.log(`Brand logo uploaded for ${brand.brandName}: ${publicUrl}`);
    } catch (err) {
      console.error("Error in uploadBrandLogo:", err);
    }
  }

  /**
   * Bulk provision multiple personas
   */
  static async bulkProvision(
    personaIds: string[],
  ): Promise<{ success: string[]; failed: Array<{ id: string; error: string }> }> {
    const results = { success: [] as string[], failed: [] as Array<{ id: string; error: string }> };

    for (const personaId of personaIds) {
      try {
        const persona = getPublicGenieById(personaId);
        if (!persona) {
          results.failed.push({ id: personaId, error: "Persona not found" });
          continue;
        }

        const existing = await this.getDemoAgentByPersonaId(personaId);
        if (existing?.elevenlabs_id) {
          results.success.push(personaId);
          continue;
        }

        await this.provisionDemoAgent(persona.id);
        results.success.push(personaId);
      } catch (error) {
        results.failed.push({ id: personaId, error: (error as Error).message });
      }
    }

    return results;
  }

  /**
   * Bulk provision all personas for an industry
   */
  static async bulkProvisionIndustry(industryId: string): Promise<{
    success: string[];
    failed: Array<{ id: string; error: string }>;
  }> {
    const allPersonas = getPublicGeniesForIndustry(industryId);
    return this.bulkProvision(allPersonas.map((p) => p.id));
  }

  // =========================================================================
  // Admin Methods
  // =========================================================================

  /**
   * Get provisioning statistics for the admin dashboard
   */
  static async getProvisioningStats(): Promise<{
    total: number;
    ready: number;
    pending: number;
  }> {
    const allDemoAgents = await this.getActiveDemoAgents();
    const totalPersonas = getAllPublicGenies().length;

    return {
      total: totalPersonas,
      ready: allDemoAgents.filter((a) => !!a.elevenlabs_id).length,
      pending: totalPersonas - allDemoAgents.length,
    };
  }

  /**
   * Re-embed brand identity into an existing demo agent.
   * Looks up the persona brand from the registry and merges brandName,
   * tagline, colors, and logo into the agent's branding field.
   */
  static async reEmbedBranding(agent: Agent): Promise<boolean> {
    if (!agent.persona_id) return false;

    const persona = getPublicGenieById(agent.persona_id);
    const brand =
      getBrandForPersona(agent.persona_id) ||
      (persona ? getBrandForSubcategory(persona.subcategoryId) : undefined);
    if (!brand) return false;

    const existingBranding = (agent.branding as Partial<GenieBranding>) || {};
    const updatedBranding: Partial<GenieBranding> = {
      ...existingBranding,
      brandName: brand.brandName,
      tagline: brand.tagline,
      primaryColor: brand.primaryColor,
      secondaryColor: brand.secondaryColor,
    };

    // Update agent record
    const { error } = await supabase
      .from("agents")
      .update({
        brand: brand.brandName,
        branding: updatedBranding,
      })
      .eq("id", agent.id);

    if (error) {
      console.error(`Failed to re-embed branding for ${agent.id}:`, error);
      return false;
    }

    // Always (re-)upload the brand logo so avatars stay current
    let logoUrl: string | null = existingBranding.logoUrl || null;
    try {
      await this.uploadBrandLogo(agent, agent.persona_id);
      // Re-fetch to pick up the newly uploaded logo URL
      const refreshed = await this.getDemoAgentByPersonaId(agent.persona_id!);
      if (refreshed) {
        logoUrl = (refreshed.branding as Partial<GenieBranding>)?.logoUrl || logoUrl;
      }
    } catch (err) {
      console.warn("Logo upload during re-embed failed (non-blocking):", err);
    }

    // Sync branding + logo to agent_pages (powers live links)
    // DEPRECATED: agent_pages table is obsolete and scheduled for deletion — do not add new usages.
    if (agent.elevenlabs_id) {
      const brandingWithLogo: Partial<GenieBranding> = {
        ...updatedBranding,
        ...(logoUrl ? { logoUrl } : {}),
      };
      const { error: pageError } = await supabase
        .from("agent_pages")
        .update({
          branding: brandingWithLogo,
          brand_color: brand.primaryColor,
          ...(logoUrl ? { genie_image_url: logoUrl } : {}),
        })
        .eq("elevenlabs_agent_id", agent.elevenlabs_id);

      if (pageError) {
        console.warn(`Failed to sync agent_pages branding for ${agent.id}:`, pageError);
      }
    }

    return true;
  }

  /**
   * Re-embed branding for ALL existing demo agents.
   * Useful for backfilling branding data on already-provisioned agents.
   */
  static async reEmbedAllBranding(): Promise<{
    updated: number;
    failed: number;
    skipped: number;
  }> {
    const agents = await this.getActiveDemoAgents();
    const results = { updated: 0, failed: 0, skipped: 0 };

    for (const agent of agents) {
      if (!agent.persona_id) {
        results.skipped++;
        continue;
      }

      const success = await this.reEmbedBranding(agent);
      if (success) {
        results.updated++;
      } else {
        results.failed++;
      }
    }

    console.log(
      `Re-embed branding complete: ${results.updated} updated, ${results.failed} failed, ${results.skipped} skipped`,
    );
    return results;
  }

}
